// src/BackgroundCheck.js
//
// Criminal screening for one stylist.
//
// Plaid told us who she is. Nothing told us whether she has a record, and she
// is going to be alone in a customer's home — so the audit's third-party
// inventory recorded "no background check" as the gap it is. This is where one
// gets recorded.
//
// Built for Checkr's hosted flow first, which is the cheap way to start: invite
// the candidate from Checkr's own dashboard, let her enter her details there
// (we never hold them), then write the outcome here. The API integration can
// replace the manual entry later without changing this schema.
//
// A check is not a boolean, which is why this screen has more than a tick box.
// Using a consumer report to decide about someone puts us under FCRA: she has
// to have signed a disclosure first, a hit has to be looked at individually
// rather than auto-rejected, and turning her down needs a pre-adverse notice,
// a waiting period, then a final one. The fields below are those steps.
//
// The badge the customer sees is derived by trigger from what is saved here —
// cleared or nothing. A customer is never shown that a check found something.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, MenuItem, Stack, TextField, Typography, Alert,
} from '@mui/material';
import { supabase } from './supabaseClient';

// Stored values on the left, what an admin reads on the right. Checkr's own
// vocabulary is kept as the stored value so a report can be matched back to
// their dashboard, but none of it is shown raw.
const PACKAGES = [
  ['basic', 'Basic'],
  ['essential', 'Essential'],
  ['complete', 'Complete'],
];
const RESULTS = [
  ['clear', 'Clear'],
  ['consider', 'Consider — needs a decision'],
];
const ADJUDICATIONS = [
  ['engaged', 'Engaged — hired anyway'],
  ['pre_adverse_action', 'Pre-adverse notice sent'],
  ['post_adverse_action', 'Final adverse notice sent'],
  ['cleared', 'Reviewed and cleared'],
];
const STATUS_LABELS = {
  pending: 'Pending', invited: 'Invitation sent', in_progress: 'In progress',
  complete: 'Complete', suspended: 'Suspended', dispute: 'Disputed', canceled: 'Cancelled',
};
const titleCase = (v) =>
  (v ? String(v).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '');
const labelFor = (pairs, value) => {
  const hit = pairs.find(([v]) => v === value);
  return hit ? hit[1] : titleCase(value);
};

const oneYearOn = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

export default function BackgroundCheck({ stylistId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Checkr's own package list, so the dropdown shows what this account
  // actually sells rather than slugs guessed from the docs.
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const [form, setForm] = useState({
    package: 'essential', result: 'clear', report_id: '',
    completed_at: new Date().toISOString().slice(0, 10),
    expires_at: oneYearOn(), adjudication: '', notes: '',
  });

  const load = useCallback(async () => {
    if (!stylistId) return;
    const { data, error: e } = await supabase
      .from('admin_background_checks')
      .select('*')
      .eq('stylist_id', stylistId)
      .order('requested_at', { ascending: false });
    if (e) { setError(e.message); return; }
    setError(null);
    setRows(data || []);
  }, [stylistId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const { error: e } = await supabase.from('background_checks').insert({
      stylist_id: stylistId,
      provider: 'checkr',
      package: form.package,
      report_id: form.report_id || null,
      status: 'complete',
      result: form.result,
      // Only meaningful on a 'consider': it records that a human looked.
      adjudication: form.adjudication || null,
      completed_at: form.completed_at ? new Date(form.completed_at).toISOString() : new Date().toISOString(),
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      notes: form.notes || null,
    });
    setSaving(false);
    if (e) { setError(e.message); return; }
    setOpen(false);
    load();
  };

  // No package choice. Every stylist gets the same screening, so the badge
  // means the same thing on every profile — a dropdown would quietly let a
  // cheaper, weaker check sit behind an identical badge.
  const sendInvite = async () => {
    setInviting(true);
    setInviteResult(null);
    const { data, error: e } = await supabase.functions.invoke('checkr-invite', {
      body: { action: 'invite', stylist_id: stylistId },
    });
    setInviting(false);
    if (e) { setInviteResult({ error: e.message }); return; }
    if (data?.error) { setInviteResult({ error: data.error, needsDisclosure: data.needs_disclosure }); return; }
    setInviteResult({ ok: true, url: data?.invitation_url, expires: data?.expires_at });
    load();
  };

  if (rows === null) return <CircularProgress size={16} sx={{ my: 1 }} />;

  return (
    <Box sx={{ my: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Background check</Typography>
        {rows.length === 0 && <Chip size="small" variant="outlined" label="None on file" />}
        {rows.map((r) => (
          <Chip
            key={r.id}
            size="small"
            variant="outlined"
            color={r.currently_clear ? 'success' : (r.result === 'consider' ? 'warning' : 'default')}
            title={[
              `Status: ${STATUS_LABELS[r.status] || titleCase(r.status)}`,
              r.result ? `Result: ${labelFor(RESULTS, r.result)}` : null,
              r.adjudication ? `Decision: ${labelFor(ADJUDICATIONS, r.adjudication)}` : null,
              r.expires_at ? `Re-check due ${new Date(r.expires_at).toLocaleDateString()}` : null,
              r.disclosure_on_file ? 'Disclosure on file' : 'No disclosure recorded',
            ].filter(Boolean).join(' · ')}
            label={
              r.currently_clear ? 'Cleared'
                : r.expired ? 'Expired'
                : r.result === 'consider' ? 'Needs review'
                : (STATUS_LABELS[r.status] || titleCase(r.status))
            }
          />
        ))}
        <Button size="small" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancel' : 'Enter a result by hand'}
        </Button>
      </Stack>

      {open && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          For a check that did not arrive on its own — one run outside LIVLIFE, or a Checkr report
          whose webhook never reached us. Copy the outcome from Checkr. An invitation sent from
          here records itself, so this is not needed for those.
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {rows.some((r) => r.result === 'consider' && r.adjudication !== 'cleared') && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          This report came back <strong>consider</strong>, which is not a rejection. Look at what it
          found against the job before deciding, and if you turn her down, send a pre-adverse notice
          with a copy of the report first and leave a waiting period before the final notice.
        </Alert>
      )}

      {/* Nothing to invite them to once they are cleared — offering it again
          invites a second paid check on someone who has already passed. */}
      {!rows.some((r) => r.currently_clear) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" disabled={inviting} onClick={sendInvite}>
            {inviting ? 'Sending…' : 'Send a Checkr invitation'}
          </Button>
          {rows.some((r) => r.expired) && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Previous check has expired — a new one is needed.
            </Typography>
          )}
        </Stack>
      )}

      {inviteResult?.error && (
        <Alert severity={inviteResult.needsDisclosure ? 'warning' : 'error'} sx={{ mb: 1 }}>
          {inviteResult.error}
        </Alert>
      )}
      {inviteResult?.ok && (
        <Alert severity="success" sx={{ mb: 1 }}>
          Invitation sent. Checkr emails the stylist a link; it expires{' '}
          {inviteResult.expires ? new Date(inviteResult.expires).toLocaleDateString() : 'in 7 days'}.
          {inviteResult.url && (
            <> <a href={inviteResult.url} target="_blank" rel="noreferrer">Open the candidate link</a>.</>
          )}
        </Alert>
      )}

      {open && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1}>
            <TextField select size="small" label="Package" value={form.package} sx={{ minWidth: 130 }}
              onChange={(e) => setForm((f) => ({ ...f, package: e.target.value }))}>
              {PACKAGES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Result" value={form.result} sx={{ minWidth: 130 }}
              onChange={(e) => setForm((f) => ({ ...f, result: e.target.value }))}>
              {RESULTS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Checkr report ID" value={form.report_id} sx={{ flex: 1 }}
              onChange={(e) => setForm((f) => ({ ...f, report_id: e.target.value }))} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="date" label="Completed" value={form.completed_at}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setForm((f) => ({ ...f, completed_at: e.target.value }))} />
            <TextField size="small" type="date" label="Re-check due" value={form.expires_at}
              InputLabelProps={{ shrink: true }}
              helperText="Badge drops automatically on this date"
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} />
            {form.result === 'consider' && (
              <TextField select size="small" label="Adjudication" value={form.adjudication} sx={{ minWidth: 180 }}
                onChange={(e) => setForm((f) => ({ ...f, adjudication: e.target.value }))}>
                <MenuItem value="">—</MenuItem>
                {ADJUDICATIONS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
            )}
          </Stack>
          <TextField size="small" multiline minRows={2} label="Notes (what was found, and why it was or was not relevant)"
            value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <Box>
            <Button size="small" variant="contained" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save check'}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
